import { Alert, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import { InputProps, ReferenceInput, required, SelectInput, useChoicesContext, useTranslate } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

function MaybeSelectInput(props: { kind: string; setTemplateAvailable: (available: boolean) => void }) {
  const { kind, setTemplateAvailable } = props;
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const { total, availableChoices, isLoading } = useChoicesContext();

  useEffect(() => {
    setValue("template", getDefaultValue());
  }, [kind, availableChoices]);

  const getDefaultValue = () => {
    if (availableChoices && availableChoices.length) {
      const defaultTemplate = availableChoices.find((t) => t.is_default);
      if (defaultTemplate) {
        return defaultTemplate.id;
      } else if (availableChoices.length === 1) {
        return availableChoices[0].id;
      }
    }
    return null;
  };

  useEffect(() => {
    setTemplateAvailable(total !== undefined && total > 0);
  }, [total, setTemplateAvailable]);

  if (total === 0) {
    const translatedKind = translate(`resources.invoices.kinds.${kind.toLowerCase()}`);
    return (
      <Grid item xs={12}>
        <Alert severity="warning">
          <Typography>
            {translate("resources.invoices.hints.no_invoice_templates_present", { kind: translatedKind })}
          </Typography>
        </Alert>
      </Grid>
    );
  }

  let sx = {};
  if (isLoading || !availableChoices || total === 1) {
    sx = { display: "none" };
  }

  return (
    <Grid item xs={12} sx={sx}>
      <SelectInput defaultValue={getDefaultValue()} validate={required()} helperText={false} />
    </Grid>
  );
}

type InvoiceTemplateInputPropsType = {
  initialKind: string;
  setTemplateAvailable: (available: boolean) => void;
} & Omit<InputProps, "source">;

export default function InvoiceTemplateInput(props: InvoiceTemplateInputPropsType) {
  const { initialKind, setTemplateAvailable, ...rest } = props;
  const invoiceKind = useWatch<{ kind: string | undefined }>({ name: "kind" });
  const kind = invoiceKind || initialKind;

  if (!kind) return null;

  return (
    <ReferenceInput
      source="template"
      filter={{ scope: kind }}
      sort={{ field: "name", order: "ASC" }}
      reference="invoice_templates"
    >
      <MaybeSelectInput setTemplateAvailable={setTemplateAvailable} kind={kind} {...rest} />
    </ReferenceInput>
  );
}
